import React, { useState } from "react";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaComment,
  FaCalendar,
  FaExclamationTriangle,
} from "react-icons/fa";

const CommentActionModal = ({ isOpen, comment, onClose, onStatusUpdate }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  if (!isOpen || !comment) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-gradient-to-r from-green-400 to-green-600 text-white";
      case "pending":
        return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-white";
      case "blocked":
        return "bg-gradient-to-r from-red-400 to-red-600 text-white";
      case "deleted":
        return "bg-gradient-to-r from-gray-400 to-gray-600 text-white";
      default:
        return "bg-gradient-to-r from-gray-400 to-gray-600 text-white";
    }
  };

  const getActionButtonStyle = (status, currentStatus) => {
    if (status === currentStatus) {
      return "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105";
    }
    return "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 hover:shadow-md transform hover:scale-105 transition-all duration-200";
  };

  const handleStatusUpdate = (status) => {
    if (status === "Blocked" || status === "Deleted") {
      setPendingAction(status);
      setShowConfirmModal(true);
    } else {
      onStatusUpdate(status);
    }
  };

  const confirmAction = () => {
    if (pendingAction) {
      onStatusUpdate(pendingAction);
      setShowConfirmModal(false);
      setPendingAction(null);
    }
  };

  const cancelAction = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-start justify-center z-50 pt-8">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 border border-gray-100 overflow-hidden">
          {/* Modal Header with gradient background */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <FaComment className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold">Comment Actions</h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
            {/* Action Buttons - Enhanced styling */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">
                <span className="bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                  Update Status
                </span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {["Approved", "Blocked", "Deleted"].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    className={`px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${getActionButtonStyle(
                      status,
                      comment.status
                    )}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Comment Details with enhanced styling */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <FaUser className="w-4 h-4 text-green-600" />
                  </div>
                  <label className="text-sm font-semibold text-gray-700">
                    Name
                  </label>
                </div>
                <p className="text-sm text-gray-900 font-medium pl-11">
                  {comment.name || "N/A"}
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <FaEnvelope className="w-4 h-4 text-green-600" />
                  </div>
                  <label className="text-sm font-semibold text-gray-700">
                    Email
                  </label>
                </div>
                <p className="text-sm text-gray-900 font-medium pl-11 break-all">
                  {comment.email || "N/A"}
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaComment className="w-4 h-4 text-purple-600" />
                  </div>
                  <label className="text-sm font-semibold text-gray-700">
                    Comment
                  </label>
                </div>
                <p className="text-sm text-gray-900 font-medium pl-11 leading-relaxed">
                  {comment.comments || "N/A"}
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaCalendar className="w-4 h-4 text-orange-600" />
                  </div>
                  <label className="text-sm font-semibold text-gray-700">
                    Current Status
                  </label>
                </div>
                <div className="pl-11">
                  <span
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${getStatusColor(
                      comment.status
                    )}`}
                  >
                    {comment.status || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer with enhanced styling */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-gray-100 overflow-hidden">
            {/* Confirmation Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <FaExclamationTriangle className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold">Confirm Action</h3>
              </div>
            </div>

            {/* Confirmation Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaExclamationTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Are you sure?
                </h4>
                <p className="text-gray-600">
                  Are you sure you want to{" "}
                  <span className="font-semibold text-red-600">
                    {pendingAction?.toLowerCase()}
                  </span>{" "}
                  this comment? This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Confirmation Footer */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelAction}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommentActionModal;
